import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Target, TrendingUp, Layers, CircleDollarSign,
  Network, Gauge, Tag, Percent, ChartColumn, Users, Truck, ShieldCheck,
  Map, MapPin, Funnel, Repeat, Award, GraduationCap, Handshake, Boxes,
  Scale, ShoppingCart, Undo2, Megaphone, MousePointerClick, Tags,
  HeartHandshake,
} from "lucide-react";

const EXAMINE_ICONS = {
  Target, TrendingUp, Layers, CircleDollarSign, Network, Gauge,
  Tag, Percent, ChartColumn, Users, Truck, ShieldCheck,
  Map, MapPin, Funnel, Repeat, Award, GraduationCap, Handshake, Boxes,
  Scale, ShoppingCart, Undo2, Megaphone, MousePointerClick, Tags,
  HeartHandshake,
};
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import NotFoundPage from "@/pages/NotFoundPage";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";
import { getSolution } from "@/data/solutions";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

export default function SolutionPage({ solutionId }) {
  const solution = getSolution(solutionId);
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  useEffect(() => {
    if (solution) track("solution_page_view", { solution_id: solution.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionId]);

  usePageMeta(
    solution
      ? {
          title: solution.seoTitle,
          description: solution.seoDescription,
          canonical: `https://blezzings.co/solutions/${solution.id}`,
          breadcrumbs: [
            { name: "Home", url: "https://blezzings.co/" },
            { name: "Solutions", url: "https://blezzings.co/solutions" },
            { name: solution.name, url: `https://blezzings.co/solutions/${solution.id}` },
          ],
          faqs: solution.faqs.map((f) => ({ question: f.q, answer: f.a })),
          service: {
            name: `BLEZZINGS ${solution.name} — ${solution.descriptor}`,
            description: solution.subhead,
            url: `https://blezzings.co/solutions/${solution.id}`,
          },
        }
      : {}
  );

  if (!solution) return <NotFoundPage />;

  const openBook = (source) => {
    track("consultation_cta_click", { solution_id: solution.id, source_page: source });
    setBookOpen(true);
  };
  const contactHref = `/contact?solution=${solution.id}&source=solution-page`;

  return (
    <div data-testid={`solution-page-${solution.id}`} className="relative bg-white">
      <Nav onBook={() => openBook("nav")} />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="pt-24 md:pt-28">
        <ol className={`${GRID} flex items-center gap-2 text-xs text-zinc-500`}>
          <li><Link to="/" className="hover:text-black transition-colors">Home</Link></li>
          <li aria-hidden>›</li>
          <li><Link to="/solutions" className="hover:text-black transition-colors">Solutions</Link></li>
          <li aria-hidden>›</li>
          <li className="text-black font-medium" aria-current="page">{solution.name}</li>
        </ol>
      </nav>

      {/* A — Hero */}
      <section className="pt-10 md:pt-14 pb-14 md:pb-20 bg-white border-b border-black/10">
        <div className={`${GRID} grid grid-cols-12 gap-8 md:gap-10 items-center lg:items-stretch`}>
          <div className="col-span-12 lg:col-span-7">
            <SectionBadge label="BLEZZINGS Solutions" />
            <h1 className="font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.04] tracking-tight">
              {solution.h1}
            </h1>
            <p className="mt-6 text-lg text-zinc-700 leading-relaxed max-w-2xl">{solution.subhead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={contactHref}
                data-testid="solution-hero-cta"
                onClick={() => track("consultation_cta_click", { solution_id: solution.id, source_page: "hero" })}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                {solution.ctaLabel} <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to={solution.tools[0].url}
                data-testid="solution-hero-tool"
                onClick={() => track("free_tool_from_solution", { solution_id: solution.id, tool_url: solution.tools[0].url })}
                className="inline-flex items-center justify-center gap-2 h-12 px-6 border border-black text-black text-sm font-medium hover:bg-black hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
              >
                Try the free tool first
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 relative flex">
            <div
              data-testid="solution-hero-visual"
              className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[380px] w-full overflow-hidden bg-zinc-200 border border-zinc-200"
            >
              <img
                src={solution.image}
                alt={solution.imageAlt}
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover slow-scale img-edit"
              />
              <div className="absolute inset-0 bg-black/10 pointer-events-none" />
              <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur-md px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-white">
                {solution.name}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* B — Sound familiar */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="solution-symptoms">
        <div className={GRID}>
          <div className="overline">Sound Familiar?</div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
            {solution.symptoms.map((s, i) => (
              <div key={i} className="border border-zinc-200 bg-white p-5 flex gap-4" data-testid={`solution-symptom-${i}`}>
                <span className="text-xs text-zinc-400 tabular-nums pt-1">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-[15px] text-zinc-700 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* C — The real problem */}
      <section className="py-14 md:py-20 bg-black text-white">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline" style={{ color: "#999" }}>The Real Problem</div>
          <p className="mt-6 font-serif-display text-2xl md:text-3xl leading-snug">{solution.perspective}</p>
        </div>
      </section>

      {/* D — How it works (5 visible stages, high-level only) */}
      <section className="py-14 md:py-20" data-testid="solution-stages">
        <div className={GRID}>
          <div className="overline">How {solution.name} Works</div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-4">
            {solution.stages.map((s, i) => (
              <div key={i} className="relative border border-zinc-200 bg-white p-5 flex items-center gap-4 md:block" data-testid={`solution-stage-${i}`}>
                <div className="hidden md:block font-serif-display text-3xl text-zinc-200">{i + 1}</div>
                <span className="md:hidden inline-flex h-8 w-8 shrink-0 items-center justify-center bg-black text-white text-xs font-semibold">{i + 1}</span>
                <p className="text-sm text-zinc-700 leading-relaxed font-medium md:mt-3">{s}</p>
                {i < solution.stages.length - 1 && (
                  <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-300 z-10" aria-hidden />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* E — What we examine */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="solution-examine">
        <div className={GRID}>
          <div className="overline">What We Examine</div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
            {solution.examine.map((e, i) => {
              const Icon = EXAMINE_ICONS[solution.examineIcons[i]] || Target;
              return (
                <div
                  key={i}
                  className="group border border-zinc-200 bg-white p-6 hover:border-black transition-colors"
                  data-testid={`solution-examine-${i}`}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center bg-black text-white">
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <p className="mt-4 text-[15px] font-semibold text-zinc-800 leading-snug">{e}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* F — What you receive */}
      <section className="py-14 md:py-20" data-testid="solution-deliverables">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline">What You Receive</div>
          <div className="mt-8 space-y-0">
            {solution.deliverables.map((d, i) => (
              <div key={i} className="flex items-baseline gap-4 border-b border-black/10 py-4" data-testid={`solution-deliverable-${i}`}>
                <span className="text-xs text-zinc-400 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[15px] text-zinc-800 font-medium">{d}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-zinc-500">Exact outputs depend on agreed scope.</p>
        </div>
      </section>

      {/* G — Who it is for + typical starting engagement */}
      <section className="py-14 md:py-20 bg-[#fafafa]">
        <div className={`${GRID} grid md:grid-cols-2 gap-8`}>
          <div className="border border-zinc-200 bg-white p-7">
            <div className="overline">Who It Is For</div>
            <p className="mt-4 text-[15px] text-zinc-700 leading-relaxed">{solution.audience}</p>
          </div>
          <div className="border border-zinc-200 bg-white p-7">
            <div className="overline">Typical Starting Engagement</div>
            <p className="mt-4 text-[15px] text-zinc-700 leading-relaxed">{solution.duration}</p>
            <p className="mt-2 text-xs text-zinc-500">Design and activation work is scoped separately, after the diagnostic.</p>
          </div>
        </div>
      </section>

      {/* H — Explore before you engage */}
      <section className="py-14 md:py-20" data-testid="solution-explore">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline">Explore Before You Engage</div>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {solution.tools.map((t) => (
              <Link
                key={t.url}
                to={t.url}
                data-testid={`solution-tool-${t.url.split("/").pop()}`}
                onClick={() => track("free_tool_from_solution", { solution_id: solution.id, tool_url: t.url })}
                className="group border border-black/15 p-6 hover:border-black transition-colors"
              >
                <div className="text-[11px] font-semibold tracking-[0.28em] text-zinc-400">FREE TOOL</div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="font-serif-display text-lg text-black leading-snug">{t.name}</span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
          {solution.toolNote && <p className="mt-4 text-xs text-zinc-500 max-w-2xl">{solution.toolNote}</p>}
          <div className="mt-6 space-y-3">
            {solution.insights.map((a) => (
              <div key={a.url} className="text-sm">
                <span className="text-zinc-400 mr-2">Read:</span>
                <Link to={a.url} className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors">
                  {a.title}
                </Link>
              </div>
            ))}
            <div className="text-sm">
              <span className="text-zinc-400 mr-2">Related capability:</span>
              <Link to={solution.relatedCapability.url} className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors">
                {solution.relatedCapability.name}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* I — How we begin */}
      <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="solution-begin">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline">How We Begin</div>
          <ol className="mt-8 space-y-4">
            {[
              { t: "Complimentary discovery", d: "A conversation about the problem, fit and available data. Not a free diagnostic or advisory report." },
              { t: "Scoped diagnostic or engagement", d: "A proposal defines scope, deliverables, responsibilities, timeline and fees before work starts. The detailed diagnostic is a paid engagement." },
              { t: "Activation, where commissioned", d: "Implementation support, measurement and capability transfer — agreed separately, with the findings in hand." },
            ].map((s, i) => (
              <li key={i} className="flex gap-4 border-b border-black/10 pb-4">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center bg-black text-white text-xs font-semibold">{i + 1}</span>
                <div>
                  <span className="text-[15px] font-semibold text-black">{s.t}</span>
                  <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20" data-testid="solution-faq">
        <div className={`${GRID} max-w-4xl`}>
          <div className="overline">Questions, Answered</div>
          <Accordion type="single" collapsible className="mt-8">
            {solution.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-black/10" data-testid={`solution-faq-${i}`}>
                <AccordionTrigger className="text-left text-[15px] font-semibold text-black hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-zinc-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* J — Bottom CTA */}
      <section className="py-16 md:py-24 bg-black text-white text-center" data-testid="solution-bottom-cta">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12">
          <h2 className="font-serif-display font-bold text-3xl md:text-4xl leading-tight">
            {solution.ctaHeading}
          </h2>
          <Link
            to={contactHref}
            data-testid="solution-bottom-cta-btn"
            onClick={() => track("consultation_cta_click", { solution_id: solution.id, source_page: "bottom" })}
            className="mt-8 inline-flex items-center justify-center gap-2 h-12 px-7 bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
          >
            {solution.ctaLabel} <ArrowUpRight className="h-4 w-4" />
          </Link>
          <p className="mt-6 text-sm text-zinc-400">
            <Link to="/how-we-engage" className="underline underline-offset-4 decoration-zinc-600 hover:decoration-white hover:text-white transition-colors">
              How engagements work
            </Link>
            {" · "}
            <Link to="/solutions" className="underline underline-offset-4 decoration-zinc-600 hover:decoration-white hover:text-white transition-colors">
              All five solutions
            </Link>
          </p>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
